import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { Button } from '../ui/button';
import { ScrollArea } from '../ui/scroll-area';
import NotificationBell from '../NotificationBell';
import axios from 'axios';
import { 
  LayoutDashboard, 
  ClipboardList, 
  LogOut, 
  Menu, 
  X,
  Globe,
  ChevronRight,
  BookOpen,
  Apple,
  Palmtree,
  MessageSquare,
  Tag
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ClientLayout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [plans, setPlans] = useState([]);
  const [unreadChat, setUnreadChat] = useState(0);
  const { user, logout } = useAuth();
  const { t, language, changeLanguage } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    fetchPlans();
    fetchUnreadChat();
    const id = setInterval(fetchUnreadChat, 15000);
    return () => clearInterval(id);
  }, [location.pathname]);

  const fetchUnreadChat = async () => {
    try {
      const r = await axios.get(`${API}/chat/unread-count`);
      setUnreadChat(r.data?.unread || 0);
    } catch {
      // silencioso
    }
  };

  const fetchPlans = async () => {
    try {
      const response = await axios.get(`${API}/plans`);
      setPlans(response.data);
    } catch (error) {
      console.error('Failed to fetch plans:', error);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Mobile Header */}
      <header className="glass-header fixed top-0 left-0 right-0 z-50 h-16 flex items-center justify-between px-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setSidebarOpen(true)}
          data-testid="client-mobile-menu"
        >
          <Menu className="h-6 w-6" />
        </Button>
        <img 
          src="https://customer-assets.emergentagent.com/job_plan-tracker-25/artifacts/7nvt53w6_Sin%20t%C3%ADtulo.jpeg" 
          alt="TRINES PORTAL 33 - FIT GUARDIAN" 
          className="h-10"
        />
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/pricing')}
            data-testid="client-header-pricing-btn"
            className="text-primary hover:bg-primary/10 px-2 h-9"
            title="Planes y precios"
          >
            <Tag className="h-4 w-4 mr-1" strokeWidth={2} />
            <span className="text-xs font-semibold hidden sm:inline">Planes</span>
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => changeLanguage(language === 'es' ? 'en' : 'es')}
          >
            <Globe className="h-5 w-5" />
          </Button>
          <NotificationBell />
        </div>
      </header>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/80 z-50"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed top-0 left-0 z-50 h-screen w-72 sidebar transform transition-transform duration-300
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="h-20 flex items-center justify-between px-6 border-b border-white/10">
            <div>
              <p className="text-sm text-muted-foreground">{t('welcomeBack')}</p>
              <p className="font-semibold text-foreground">{user?.name}</p>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="h-5 w-5" />
            </Button>
          </div>

          {/* Navigation */}
          <ScrollArea className="flex-1 py-6">
            <nav className="space-y-1 px-3">
              <Button
                variant="ghost"
                className={`w-full justify-start ${location.pathname === '/portal' ? 'nav-active text-primary' : 'text-muted-foreground'}`}
                onClick={() => { navigate('/portal'); setSidebarOpen(false); }}
                data-testid="client-nav-dashboard"
              >
                <LayoutDashboard className="h-5 w-5 mr-3" strokeWidth={1.5} />
                {t('myTraining')}
              </Button>

              <Button
                variant="ghost"
                className={`w-full justify-start ${location.pathname === '/portal/recursos' ? 'nav-active text-primary' : 'text-muted-foreground'}`}
                onClick={() => { navigate('/portal/recursos'); setSidebarOpen(false); }}
                data-testid="client-nav-recursos"
              >
                <BookOpen className="h-5 w-5 mr-3" strokeWidth={1.5} />
                Centro de Recursos
              </Button>

              <Button
                variant="ghost"
                className={`w-full justify-start ${location.pathname === '/portal/nutricion' ? 'nav-active text-primary' : 'text-muted-foreground'}`}
                onClick={() => { navigate('/portal/nutricion'); setSidebarOpen(false); }}
                data-testid="client-nav-nutricion"
              >
                <Apple className="h-5 w-5 mr-3" strokeWidth={1.5} />
                Nutrición
              </Button>

              <Button
                variant="ghost"
                className={`w-full justify-start ${location.pathname === '/portal/disena-entreno' ? 'nav-active text-primary' : 'text-muted-foreground'}`}
                onClick={() => { navigate('/portal/disena-entreno'); setSidebarOpen(false); }}
                data-testid="client-nav-disena-entreno"
              >
                <Palmtree className="h-5 w-5 mr-3" strokeWidth={1.5} />
                Diseña tu Entreno
              </Button>

              <Button
                variant="ghost"
                className={`w-full justify-start relative ${location.pathname === '/portal/chat' ? 'nav-active text-primary' : 'text-muted-foreground'}`}
                onClick={() => { navigate('/portal/chat'); setSidebarOpen(false); }}
                data-testid="client-nav-chat"
              >
                <MessageSquare className="h-5 w-5 mr-3" strokeWidth={1.5} />
                Mensajes
                {unreadChat > 0 && (
                  <span className="ml-auto min-w-[20px] h-5 px-1.5 rounded-full bg-red-500 text-white text-xs font-bold flex items-center justify-center" data-testid="client-chat-badge">
                    {unreadChat > 9 ? '9+' : unreadChat}
                  </span>
                )}
              </Button>

              <Button
                variant="ghost"
                className={`w-full justify-start ${location.pathname === '/pricing' ? 'nav-active text-primary' : 'text-muted-foreground'}`}
                onClick={() => { navigate('/pricing'); setSidebarOpen(false); }}
                data-testid="client-nav-pricing"
              >
                <Tag className="h-5 w-5 mr-3" strokeWidth={1.5} />
                Planes y precios
              </Button>

              <Button
                variant="ghost"
                className={`w-full justify-start ${location.pathname === '/portal/suscripcion' ? 'nav-active text-primary' : 'text-muted-foreground'}`}
                onClick={() => { navigate('/portal/suscripcion'); setSidebarOpen(false); }}
                data-testid="client-nav-subscription"
              >
                <Tag className="h-5 w-5 mr-3" strokeWidth={1.5} />
                Mi suscripción
              </Button>

              {/* Plans List - HIDDEN FROM CLIENTS - They only see "Session of the Day" */}
              {/* Admin can see full plans from their panel */}
            </nav>
          </ScrollArea>

          {/* Footer */}
          <div className="p-4 border-t border-white/10 space-y-2">
            <Button
              variant="ghost"
              className="w-full justify-start text-muted-foreground hover:text-foreground"
              onClick={() => changeLanguage(language === 'es' ? 'en' : 'es')}
            >
              <Globe className="h-5 w-5 mr-3" strokeWidth={1.5} />
              {language === 'es' ? 'English' : 'Español'}
            </Button>
            <Button
              variant="ghost"
              className="w-full justify-start text-muted-foreground hover:text-destructive"
              onClick={handleLogout}
              data-testid="client-logout-button"
            >
              <LogOut className="h-5 w-5 mr-3" strokeWidth={1.5} />
              {t('logout')}
            </Button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="pt-16 pb-6">
        <div className="max-w-2xl mx-auto px-4">
          {children}
        </div>
      </main>
    </div>
  );
};

export default ClientLayout;
